import {
    FPSR_CDN_ASSETS_BASE,
    cdnTokenFetch,
} from '@/components/fpsr-viewer/cdn-assets';
import type {
    MaxOutputSize,
    RenderBackground,
    Renderer,
} from '@rickyzhangca/fpsr';

export { FPSR_CDN_ASSETS_BASE };
export const FPSR_THUMBNAIL_MAX: MaxOutputSize = { width: 1080, height: 1080 };

export const FPSR_META_TAG = 'fpsr';
export const FPSR_PATH_META_PREFIX = 'fpsr-path:';

export const DEFAULT_FPSR_BACKGROUND: RenderBackground = { type: 'auto' };

const PLANET_NAMES = new Set([
  'nauvis',
  'vulcanus',
  'fulgora',
  'gleba',
  'aquilo',
]);

function isRgbaTuple(
  value: unknown
): value is [number, number, number, number] {
  return (
    Array.isArray(value) &&
    value.length === 4 &&
    value.every(n => typeof n === 'number' && Number.isFinite(n))
  );
}

/** Validate a stored jsonb value as {@link RenderBackground}; fall back to auto. */
export function parseFpsrBackground(raw: unknown): RenderBackground {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return DEFAULT_FPSR_BACKGROUND;
  }
  const type = (raw as { type?: unknown }).type;
  if (type === 'none' || type === 'checkerboard' || type === 'auto') {
    return { type };
  }
  if (type === 'solid') {
    const color = (raw as { color?: unknown }).color;
    if (isRgbaTuple(color)) return { type: 'solid', color };
    return DEFAULT_FPSR_BACKGROUND;
  }
  if (type === 'terrain') {
    const name = (raw as { name?: unknown }).name;
    if (typeof name === 'string' && name.length > 0) {
      return { type: 'terrain', name };
    }
    return DEFAULT_FPSR_BACKGROUND;
  }
  if (type === 'space') {
    const planet = (raw as { planet?: unknown }).planet;
    const planetName = (raw as { planetName?: unknown }).planetName;
    const bg: Extract<RenderBackground, { type: 'space' }> = { type: 'space' };
    if (planet === true) {
      bg.planet = true;
      if (typeof planetName === 'string' && PLANET_NAMES.has(planetName)) {
        bg.planetName = planetName;
      }
    }
    return bg;
  }
  return DEFAULT_FPSR_BACKGROUND;
}

/** Stable key for comparing / labeling a RenderBackground choice. */
export function fpsrBackgroundKey(bg: RenderBackground): string {
  switch (bg.type) {
    case 'none':
    case 'checkerboard':
    case 'auto':
      return bg.type;
    case 'solid':
      return `solid:${bg.color.join(',')}`;
    case 'terrain':
      return `terrain:${bg.name}`;
    case 'space':
      return bg.planet ? `space:${bg.planetName ?? 'default'}` : 'space';
  }
}

/** Numeric segments from a flat book path like `book/0/book/1/blueprint` → `[0, 1]`. */
export function flatPathToBlueprintPath(path: string): number[] | undefined {
  const indices = path
    .split('/')
    .filter(part => /^\d+$/.test(part))
    .map(Number);
  return indices.length > 0 ? indices : undefined;
}

export function parseFpsrPathFromMeta(
  meta: string[] | null | undefined
): number[] | undefined {
  const entry = meta?.find(m => m.startsWith(FPSR_PATH_META_PREFIX));
  if (!entry) return undefined;
  const raw = entry.slice(FPSR_PATH_META_PREFIX.length);
  if (!raw) return undefined;
  const indices = raw.split(',').map(Number);
  if (indices.some(n => !Number.isInteger(n))) return undefined;
  return indices;
}

export function isFpsrTagged(meta: string[] | null | undefined): boolean {
  return !!meta?.includes(FPSR_META_TAG);
}

export function stripFpsrMeta(meta: string[] | null | undefined): string[] {
  return (meta ?? []).filter(
    m => m !== FPSR_META_TAG && !m.startsWith(FPSR_PATH_META_PREFIX)
  );
}

export function buildFpsrMeta(
  existing: string[] | null | undefined,
  opts: { tagged: boolean; path?: number[] | null }
): string[] {
  const base = stripFpsrMeta(existing);
  if (!opts.tagged) return base;
  return [
    ...base,
    FPSR_META_TAG,
    ...(opts.path?.length
      ? [`${FPSR_PATH_META_PREFIX}${opts.path.join(',')}`]
      : []),
  ];
}

let rendererPromise: Promise<Renderer> | null = null;

async function getRenderer(): Promise<Renderer> {
  if (!rendererPromise) {
    rendererPromise = (async () => {
      const { cdnAssets, createRenderer } = await import('@rickyzhangca/fpsr');
      const fetchImpl = cdnTokenFetch();
      return createRenderer({
        assets: cdnAssets(FPSR_CDN_ASSETS_BASE, {
          ...(fetchImpl ? { fetchImpl } : {}),
        }),
      });
    })().catch(error => {
      rendererPromise = null;
      throw error;
    });
  }
  return rendererPromise;
}

export type FpsrRenderOptions = {
  blueprintString: string;
  blueprintPath?: number[];
  maxOutputSize: MaxOutputSize;
  background?: RenderBackground;
  signal?: AbortSignal;
};

export async function renderFpsrThumbnailFile(
  opts: Omit<FpsrRenderOptions, 'maxOutputSize'> & {
    maxOutputSize?: MaxOutputSize;
  }
): Promise<File> {
  const { decode } = await import('@rickyzhangca/fpsr');
  const renderer = await getRenderer();
  const doc = decode(opts.blueprintString);
  const result = await renderer.render(doc, {
    blueprintPath: opts.blueprintPath,
    pixelsPerTile: 64,
    maxOutputSize: opts.maxOutputSize ?? FPSR_THUMBNAIL_MAX,
    background: opts.background ?? DEFAULT_FPSR_BACKGROUND,
    signal: opts.signal,
  });
  const blob = await result.toImageBlob({ type: 'image/webp', quality: 0.85 });
  return new File([blob], 'blueprint-render.webp', { type: 'image/webp' });
}
